import { useCallback, useEffect, useId, useRef } from 'react';
import type { JSX } from 'react';
import '../css/responsive-legend.css';
import { LegendItem } from '../LegendItems/LegendItem';
import type { LegendItem as LegendItemData } from '../LegendItems/LegendItem.types';
import { useLegendOverflow } from './useLegendOverflow';
import type { HorizontalLegendProps } from './HorizontalLegend.types';

const EMPTY_LEGEND_ITEMS: LegendItemData[] = [];

export const HorizontalLegend = ({
  children = null,
  className,
  unit,
  handleClick,
  checkIfSelected,
  checkIfFiltered,
  selected,
  grouping_key,
  legend_items
}: HorizontalLegendProps): JSX.Element | null => {
  const holder = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { overflowing, isMeasured } = useLegendOverflow(holder, legend_items ?? EMPTY_LEGEND_ITEMS);
  const setMenuOpen = useCallback((is_open: boolean) => {
    const root = holder.current;
    const more = root?.querySelector<HTMLElement>("[data-id='legend-more']");
    const button = root?.querySelector<HTMLButtonElement>("[data-id='legend-more-button']");
    const menu = root?.querySelector<HTMLUListElement>("[data-id='legend-menu']");
    if (!more || !button || !menu) return;

    more.dataset.open = String(is_open);
    button.setAttribute('aria-expanded', String(is_open));
    menu.setAttribute('aria-hidden', String(!is_open));
  }, []);
  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
  const openMenu = useCallback(() => setMenuOpen(true), [setMenuOpen]);

  useEffect(() => {
    const root = holder.current;
    const closeOutside = (event: PointerEvent) => {
      if (!root?.contains(event.target as Node)) closeMenu();
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const menu = root?.querySelector("[data-id='legend-menu']");
      const button = root?.querySelector<HTMLButtonElement>("[data-id='legend-more-button']");
      if (menu?.contains(document.activeElement)) button?.focus();
      closeMenu();
    };

    window.addEventListener('pointerdown', closeOutside, true);
    window.addEventListener('keydown', closeEscape, true);
    return () => {
      window.removeEventListener('pointerdown', closeOutside, true);
      window.removeEventListener('keydown', closeEscape, true);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!overflowing.length) closeMenu();
  }, [overflowing.length, closeMenu]);

  if (!legend_items?.length) return null;

  const renderItem = (item: LegendItemData) => {
    const is_selected = checkIfSelected?.(
      item,
      'legend',
      selected,
      { selected, grouping_key, unit},
    );

    return (
      <LegendItem
        {...item}
        item={item}
        is_selected={is_selected}
        handleClick={handleClick}
        checkIfSelected={checkIfSelected}
        checkIfFiltered={checkIfFiltered}
        selected={selected}
        grouping_key={grouping_key}
        chart_unit={unit}
        unit={item.unit ?? unit}
      />
    );
  };

  return (
    <div
      ref={holder}
      data-measured={isMeasured}
      className={['responsive-legend', 'responsive-legend--horizontal', className].filter(Boolean).join(' ')}
    >
      <div data-area='items' data-id='legend'>
        {legend_items.map((item, index) => {
          if (overflowing.includes(index)) return null;
          return (
            <div data-legend-index={index} key={String(item.label)}>
              {renderItem(item)}
            </div>
          );
        })}
      </div>
      <div
        data-area='more'
        data-id='legend-more'
        data-shown={overflowing.length > 0}
        data-open='false'
        aria-hidden={!overflowing.length}
        onMouseEnter={openMenu}
        onMouseLeave={() => {
          const menu = holder.current?.querySelector("[data-id='legend-menu']");
          if (!menu?.contains(document.activeElement)) closeMenu();
        }}
        onFocus={openMenu}
        onBlur={(event) => {
          const focus_remains_inside = event.currentTarget.contains(event.relatedTarget as Node | null);
          const pointer_remains_inside = event.currentTarget.matches(':hover');
          if (!focus_remains_inside && !pointer_remains_inside) closeMenu();
        }}
      >
        <button
          type='button'
          data-id='legend-more-button'
          tabIndex={overflowing.length ? 0 : -1}
          aria-label={`${overflowing.length} more legend items`}
          aria-expanded='false'
          aria-controls={menuId}
          onClick={openMenu}
        >
          <span aria-hidden='true'>···</span>
          <svg aria-hidden='true' focusable='false' viewBox='0 0 12 12'>
            <path d='m2.5 4.5 3.5 3 3.5-3' />
          </svg>
        </button>
        <ul
          id={menuId}
          data-id='legend-menu'
          aria-label='More legend items'
          aria-hidden='true'
          onClick={closeMenu}
        >
          {overflowing.map((index) => {
            const item = legend_items[index];
            if (!item) return null;
            return (
              <li data-legend-index={index} key={String(item.label)}>
                {renderItem(item)}
              </li>
            );
          })}
        </ul>
      </div>
      {children}
    </div>
  );
};
